import type { MagicJumpItem } from '@/api/types/magic-jump';
import * as typeCheck from './type';

/** 分享配置项 */
export interface SimpleShareOptions {
  url?: string;
  title?: string;
  content?: string;
  pic?: string;
}

/** SimpleShare 实例，提供各平台分享方法 */
export interface SimpleShareInstance {
  googleplus: () => void;
  facebook: () => void;
  twitter: () => void;
  pinterest: () => void;
}

/** SimpleShare 构造函数类型，用于 new SimpleShare(options) */
export interface SimpleShareConstructor {
  new (options?: SimpleShareOptions): SimpleShareInstance;
}

/** FB 分享配置项 */
export interface FBShareOptions {
  url?: string;
}

type StorageType = 'localStorage' | 'sessionStorage';
// 分享

/** 分享构造函数实现（仅供内部实现使用，对外类型以 SimpleShareConstructor 为准） */
function SimpleShareImpl(this: SimpleShareInstance, options?: SimpleShareOptions): void {
  options = options || {};
  let url = options.url || (typeof window !== 'undefined' ? window.location.href : '');
  let title = options.title || (typeof document !== 'undefined' ? document.title : '');
  let content = options.content || '';
  let pic = options.pic || '';
  url = encodeURIComponent(url);
  title = encodeURIComponent(title);
  content = encodeURIComponent(content);
  pic = encodeURIComponent(pic);
  const facebook = 'https://www.facebook.com/sharer/sharer.php?u={url}&t={title}';
  const twitter = 'https://x.com/intent/post?text={title}&url={url}';
  const googleplus = 'https://plus.google.com/share?title={title}&url={url}';
  const pinterest = 'https://www.pinterest.com/pin/create/button/?url={url}';
  function replaceAPI(api: string): string {
    return api
      .replace('{url}', url)
      .replace('{title}', title)
      .replace('{content}', content)
      .replace('{pic}', pic);
  }
  function popopen(targetUrl: string) {
    window.open(
      replaceAPI(targetUrl),
      't',
      'toolbar=0,resizable=1,status=0,width=640,height=528,left=500,top=300',
    );
  }
  this.googleplus = () => popopen(googleplus);
  this.facebook = () => popopen(facebook);
  // 注意：twitter 需要使用 a 链接进行分享，使用js分享在fb内置浏览器中会出现从twitter返回后页面白屏的问题
  this.twitter = () => popopen(twitter);
  this.pinterest = () => popopen(pinterest);
}

// Ps: 实测，功能正常
/** 分享：需通过 new SimpleShare(options) 使用，得到实例后调用 .facebook() / .twitter() 等 */
export const SimpleShare: SimpleShareConstructor =
  SimpleShareImpl as unknown as SimpleShareConstructor;

// fb 分享
export function FBShare(options?: FBShareOptions): void {
  options = options || {};
  const url = options.url ?? (typeof window !== 'undefined' ? window.location.href : '');
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    't',
    'toolbar=0,resizable=1,status=0,width=640,height=528,left=500,top=300',
  );
}

/**
 * @description: 递归去除值为null或者undefined的字段
 * @param obj 待处理对象
 * @return 处理后的对象（原地修改并返回）
 */
export const filterNull = <T extends Record<string, unknown>>(obj: T): T => {
  if (!typeCheck.isObject(obj)) {
    console.error('参数必须是一个对象');
    return obj;
  }
  Object.keys(obj).forEach((key) => {
    if (typeCheck.isNull(obj[key])) {
      delete obj[key];
    } else if (typeCheck.isObject(obj[key])) {
      filterNull(obj[key] as Record<string, unknown>);
    }
  });
  return obj;
};

/* 根据魔方后台的跳转对象得到跳转URL */
export function formatUrl(params: MagicJumpItem): string {
  const { jump_type, Jump_target } = params;
  // jump_type 跳转类型 number: 1 商品分组，2销售sku，3 系统页面，4 指定链接，5弹框
  if (!jump_type || !Jump_target) {
    return '';
  }
  let url = '';
  switch (jump_type) {
    case 1:
      url = `/goods-list/${Jump_target}`;
      break;
    case 2:
      url = `/goods-detail/${Jump_target}`;
      break;
    case 3:
      url = String(Jump_target);
      break;
    case 4:
      url = String(Jump_target);
      break;
    case 5:
      if (Jump_target === 1) {
        url = 'register';
      } else if (Jump_target === 2) {
        url = 'coupon';
      }
      break;
    default:
      url = '';
      break;
  }
  return url;
}

/* 根据后台配置进行跳转的方法 */
export function jumpToTarget(item: MagicJumpItem, callback: (url: string) => void): void {
  const url = formatUrl(item);
  if (!url) return;
  if (item.jump_type === 4) {
    window.location.href = url;
    return;
  }
  if (item.jump_type === 5 && callback) {
    callback(url);
    return;
  }
  navigateTo(url);
}

/**
 * @description: 内部方法：设置缓存到 Storage 中
 * @param key 必传. 要设置的key
 * @param value 必传，且不能为null. 要设置的value
 * @param type 必传. localStorage/sessionStorage
 * @return true:设置成功  false:设置不成功
 */
function setStorage(key: string, value: unknown, type: StorageType = 'localStorage'): boolean {
  try {
    if (process.server) {
      console.error('setStorage--error: 服务端禁用.', key, value);
      return false;
    }
    if (typeof key !== 'string' || typeof value === 'undefined' || value === null) {
      console.error(
        'setStorage--error: key、value为必传参数，key必须是字符串，value不允许为null。',
        key,
        value,
      );
      return false;
    }

    if (process.client) {
      window[type].setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      return true;
    }
  } catch (error) {
    console.error('setStorage--error', error, key, value, type);
  }
  return false;
}
// 设置缓存到 localStorage 中
export function setLocal(key: string, value: unknown): boolean {
  return setStorage(key, value, 'localStorage');
}
// 设置缓存到 sessionStorage 中
export function setSession(key: string, value: unknown): boolean {
  return setStorage(key, value, 'sessionStorage');
}

/**
 * @description: 内部方法：从 Storage 中读取缓存
 * @param key 必传. 要设置的key
 * @param defaultValue 必传，且不能为null. 默认的缓存值. 作用: 1. 决定是否解析缓存; 2. 取不到缓存时返回该值
 * @param type 必传. localStorage/sessionStorage
 * @return 有缓存时返回缓存，无缓存返回 defaultValue 或空字符串
 */
function getStorage<T>(
  key: string,
  defaultValue: T = '' as unknown as T,
  type: StorageType = 'localStorage',
): T | '' {
  try {
    if (process.server) {
      console.error('getStorage--error: 服务端禁用.', key, defaultValue);
      return '';
    }
    if (typeof key !== 'string' || typeof defaultValue === 'undefined' || defaultValue === null) {
      console.error(
        'getStorage--error: key、defaultValue为必传参数，key必须是字符串，value不允许为null。',
        key,
        defaultValue,
      );
      return '';
    }

    if (process.client) {
      const storageValue = (window as Window & Record<StorageType, Storage>)[type].getItem(key);
      const result = storageValue
        ? typeof defaultValue === 'string'
          ? storageValue
          : (JSON.parse(storageValue) as T)
        : defaultValue;

      return result as T | '';
    }
  } catch (error) {
    console.error('getLocal--error', error, key, defaultValue);
  }
  return '';
}
// 从 localStorage 中读取缓存
export function getLocal<T>(key: string, defaultValue: T): T | '' {
  return getStorage(key, defaultValue, 'localStorage');
}
// 从 sessionStorage 中读取缓存
export function getSession<T>(key: string, defaultValue: T): T | '' {
  return getStorage(key, defaultValue, 'sessionStorage');
}

/**
 * @description: 删除缓存
 * @param key 要删除的key
 * @param type 要删除的类型: localStorage; sessionStorage
 * @return 是否成功: true-成功; false-失败
 */
function removeStorage(key: string, type: StorageType = 'localStorage'): boolean {
  try {
    if (process.server) {
      console.error('removeStorage--error: 服务端禁用.', key);
      return false;
    }
    if (typeof key !== 'string') {
      console.error('removeStorage--error: key必须是字符串.', key);
      return false;
    }

    if (process.client) {
      window[type].removeItem(key);
      return true;
    }
  } catch (error) {
    console.error('removeStorage--error:未知错误', error, key, type);
  }
  return false;
}
export function removeLocal(key: string): boolean {
  return removeStorage(key, 'localStorage');
}
export function removeSession(key: string): boolean {
  return removeStorage(key, 'sessionStorage');
}

/**
 * 检查当前UA是否为爬虫
 * @returns 是否为爬虫
 */
export const isBot = (): boolean => {
  if (typeof window === 'undefined') return false;

  const botPatterns = [
    'googlebot',
    'bingbot',
    'baiduspider',
    'yandexbot',
    'duckduckbot',
    'sogou',
    '360spider',
    'slurp',
    'adsbot-google',
    'facebookexternalhit',
    'semrushbot',
    'ahrefsbot',
    'mj12bot',
    'crawler',
    'spider',
    'bot',
  ];

  const userAgent = navigator.userAgent.toLowerCase();
  return botPatterns.some((pattern) => userAgent.includes(pattern));
};

// 格式化数字为两位数
export const formatTimeNum = (num: number): string => {
  return num > 9 ? `${num}` : `0${num}`;
};

/**
 * 获取页面可视区域数量
 * @returns {number} 页面可视区域数量，基于当前滚动位置和可视区域高度计算
 */
export const getViewArea = (scrollTop: number): number => {
  if (typeof window === 'undefined') return 1;

  try {
    // 获取主容器元素
    const mainBox = document.body;
    if (!mainBox) return 1;

    // 获取当前滚动位置
    // const scrollTop =
    //   window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // 获取头部高度
    const headerElement = document.getElementById('main-page-header');
    const topHeaderHeight = headerElement ? headerElement.offsetHeight : 0;
    // 计算可视区域高度
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    // 视口高度（减去头部高度）
    const pageMainScroll =
      viewportHeight - topHeaderHeight > 0 ? viewportHeight - topHeaderHeight : viewportHeight;
    if (pageMainScroll <= 0) return 1;

    // 计算可视区域数量：当前滚动位置 / 可视区域高度 + 1（当前可视区域）
    const viewArea = Math.floor(scrollTop / pageMainScroll) + 1;
    return Math.max(1, viewArea); // 确保至少返回1
  } catch (error) {
    console.error('Error calculating view area:', error);
    return 1;
  }
};
